import React, { useState } from "react";
import { toast } from "sonner";
import { Eye, Film, Lock, ShieldAlert } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { STEPS, BLOCK_NAMES, fmtMealTimes } from "../../lib/onboardingSchema";
import { Card } from "./adminUi";

const fmt = (v) => {
  if (v === "yes") return "Sí";
  if (v === "no") return "No";
  if (Array.isArray(v)) return v.map((x) => (typeof x === "object" ? Object.values(x).filter(Boolean).join(" · ") : x)).join(v.some((x) => typeof x === "object") ? "\n" : ", ");
  if (v && typeof v === "object" && ("default" in v || "varies" in v)) return fmtMealTimes(v);
  if (v && typeof v === "object") return Object.entries(v).map(([d, s]) => `${d}: ${[s.time, s.duration].filter(Boolean).join(" · ")}`).join("\n");
  return String(v);
};

const Row = ({ label, value }) => (
  <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 260px) 1fr", gap: 12, padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
    <div style={{ fontSize: 12, color: "#a3a3a3" }}>{label}</div>
    <div style={{ fontSize: 14, color: "#e5e5e5", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{fmt(value)}</div>
  </div>
);

const empty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length) || (typeof v === "object" && !Array.isArray(v) && !Object.keys(v).length);

const BlockView = ({ b, data }) => {
  const fields = STEPS.filter((s) => s.b === b).flatMap((s) => s.fields).filter((f) => f.type !== "videos" && !empty(data?.[f.k]));
  return (
    <div style={{ marginBottom: 18 }} data-testid={`onb-answers-${b}`}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{b}. {BLOCK_NAMES[b]}</div>
      {fields.length === 0 ? <div style={{ fontSize: 13, color: "#737373" }}>Sin respuestas todavía.</div> : fields.map((f) => <Row key={f.k} label={f.label} value={data[f.k]} />)}
    </div>
  );
};

const VideoLink = ({ oid, f }) => {
  const open = async () => {
    try {
      const r = await api.get(`/admin/onboardings/${oid}/files/${f.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };
  return <button className="qv-btn-ghost" onClick={open} style={{ padding: "8px 12px", fontSize: 12 }} data-testid={`onb-admin-video-${f.id}`}><Film size={14} /> {f.original_filename}</button>;
};

export const AnswersView = ({ o }) => (
  <Card title="Respuestas del formulario" testid="onb-answers-card">
    {Object.keys(BLOCK_NAMES).filter((b) => b !== "E").map((b) => <BlockView key={b} b={b} data={o.form?.[b]} />)}
    {(o.files || []).length > 0 && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="onb-admin-videos">{o.files.map((f) => <VideoLink key={f.id} oid={o.id} f={f} />)}</div>
    )}
  </Card>
);

export const HealthView = ({ o }) => {
  const [h, setH] = useState(null);
  const load = () => api.get(`/admin/onboardings/${o.id}/health`).then((r) => setH(r.data)).catch((e) => toast.error(formatErr(e)));
  return (
    <Card title="Lesiones y seguridad · confidencial" testid="onb-health-card"
      right={!h && <button className="qv-btn-ghost" onClick={load} style={{ padding: "8px 12px", fontSize: 12 }} data-testid="onb-health-show"><Eye size={14} /> Mostrar</button>}>
      {o.health_review?.needed && (
        <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.4)", color: "#fecaca", padding: "10px 14px", fontSize: 13, marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }} data-testid="onb-health-review">
          <ShieldAlert size={14} style={{ flexShrink: 0 }} /> <span>Requiere revisión humana antes de planificar. La app no diagnostica ni prescribe rehabilitación.</span>
        </div>
      )}
      {!h ? (
        <div style={{ fontSize: 13, color: "#737373" }}><Lock size={13} style={{ verticalAlign: -2 }} /> Solo tú puedes ver esta información. Cada consulta queda registrada.</div>
      ) : (
        <>
          {h.flags?.length > 0 && <ul style={{ margin: "0 0 12px 18px", color: "#fca5a5", fontSize: 13 }} data-testid="onb-health-flags">{h.flags.map((f) => <li key={f}>{f}</li>)}</ul>}
          <BlockView b="E" data={h.data} />
        </>
      )}
    </Card>
  );
};
